/* eslint camelcase: "off" */
/**
 * Sessions list — one row per identity-flow session, aggregating selfie
 * + ID attempts that share a `flow_session_id` into a single row.
 *
 * Distinct from the Uploads tab (per-document fraud queue) and the
 * Documents tab (per-document compliance / retention surface). Reviewers
 * triage by session here, then drill into the SessionDetailPage for the
 * per-document evidence trail.
 *
 * A "session" is one Meta Flow launch — the selfie flow and the ID flow
 * are separate sessions even when an employee runs both back-to-back.
 * Cross-flow journey linkage is a separate concern (would need a parent
 * journey_id minted by the gate); not modelled today.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  DateRangePicker,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
} from '@jem-open/jem-ui';
import { Eye, Loader2 } from 'lucide-react';
import { useHistory } from 'react-router-dom';

import apiResponseHandler from '../../../../../utils/apiResponseHandler';
import {
  FlowSessionListItem,
  FlowSessionOutcome,
  verificationClient,
} from '../../../../../services/verificationClient';
import { severityTagVariant } from './severity';


type DateRange = { from: Date | undefined; to?: Date };


const OUTCOME_VARIANT: Record<FlowSessionOutcome, 'success' | 'failed' | 'pending' | 'processing'> = {
  matched: 'success',
  mismatch: 'failed',
  pending: 'pending',
  inconclusive: 'processing',
};


function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs === 0 ? `${mins}m` : `${mins}m ${secs}s`;
}


const SessionsListTab: React.FC = () => {
  const history = useHistory();
  const requestIdRef = useRef(0);
  const [employeeIdFilter, setEmployeeIdFilter] = useState('');
  const [dateRange, setDateRange] = useState<DateRange | null>(null);

  const [rows, setRows] = useState<FlowSessionListItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Cursor handled by caller so "Load more" doesn't race against state
  // updates — same pattern as the Uploads tab.
  const load = useCallback(
    async (resetCursor: boolean, cursorOverride?: string | null) => {
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      setLoading(true);
      try {
        const params: Parameters<typeof verificationClient.listFlowSessions>[0] = {};
        if (employeeIdFilter) params.employee_id = parseInt(employeeIdFilter, 10);
        if (dateRange?.from) params.since = dateRange.from.toISOString();
        if (dateRange?.to) params.until = dateRange.to.toISOString();
        if (!resetCursor && cursorOverride) params.cursor = cursorOverride;
        const page = await verificationClient.listFlowSessions(params);
        // Drop stale responses if filters changed mid-fetch.
        if (requestId !== requestIdRef.current) return;
        setRows((prev) => (resetCursor ? page.results : [...prev, ...page.results]));
        setNextCursor(page.next_cursor);
      } catch (err) {
        apiResponseHandler.handleApiError(err);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [employeeIdFilter, dateRange]
  );

  useEffect(() => {
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeIdFilter, dateRange]);

  const handleReset = () => {
    setDateRange(null);
    setEmployeeIdFilter('');
  };

  return (
    <div>
      {/* Filters */}
      <div className="mb-md flex flex-wrap items-end gap-xs">
        <div>
          <DateRangePicker
            label="Date range"
            dateRange={dateRange ?? undefined}
            onDateRangeChange={(range) => setDateRange((range as DateRange | undefined) ?? null)}
            placeholder="Pick a date range"
          />
        </div>
        <div className="flex flex-col gap-xxs">
          <span className="text-sm font-medium text-greyscale-text-title">Employee ID</span>
          <Input
            type="number"
            className="w-[160px]"
            value={employeeIdFilter}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEmployeeIdFilter(e.target.value)
            }
            placeholder="e.g. 102837"
          />
        </div>
        <Button variant="outline" onClick={handleReset}>
          Reset
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-greyscale-border-default bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Last activity</TableHead>
              <TableHead>Employee</TableHead>
              <TableHead>Outcome</TableHead>
              <TableHead>Worst severity</TableHead>
              <TableHead>Captures</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.flow_session_id}>
                <TableCell className="text-xs">
                  {row.last_activity_at
                    ? new Date(row.last_activity_at).toLocaleString()
                    : '—'}
                </TableCell>
                <TableCell>
                  <div className="text-xs font-medium">
                    {row.employee_name || (
                      <span className="text-greyscale-text-caption">—</span>
                    )}
                  </div>
                  <div className="text-xxs text-greyscale-text-caption">
                    {row.employer_name ? `${row.employer_name} · ` : ''}#{row.employee_id}
                  </div>
                </TableCell>
                <TableCell>
                  <Tag variant={OUTCOME_VARIANT[row.outcome]}>{row.outcome}</Tag>
                </TableCell>
                <TableCell>
                  {row.worst_severity ? (
                    <Tag variant={severityTagVariant(row.worst_severity)}>
                      {row.worst_severity}
                    </Tag>
                  ) : (
                    <span className="text-xxs text-greyscale-text-caption">—</span>
                  )}
                </TableCell>
                <TableCell className="text-xs">
                  <div>
                    {row.selfie_present ? '1 selfie' : 'no selfie'}
                    {' · '}
                    {row.id_attempts_count} ID
                    {row.id_attempts_count === 1 ? ' attempt' : ' attempts'}
                  </div>
                </TableCell>
                <TableCell className="text-xs">
                  {formatDuration(row.duration_seconds)}
                </TableCell>
                <TableCell>
                  <Button
                    variant="link"
                    size="xs"
                    onClick={() =>
                      history.push(`/admin/security/sessions/${row.flow_session_id}`)
                    }
                    leftIcon={<Eye size={14} />}
                  >
                    view
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} className="py-md text-center text-greyscale-text-caption">
                  No sessions match.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {loading && (
          <div className="flex justify-center py-sm">
            <Loader2 className="animate-spin text-secondary-text-label" size={20} />
          </div>
        )}
        {nextCursor && (
          <div className="flex justify-center py-sm">
            <Button
              variant="link"
              onClick={() => load(false, nextCursor)}
              disabled={loading}
            >
              Load more →
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionsListTab;
